// useIntlAccaPass — PRE_TOURNAMENT / ACTIVE tournaments + the current
// user's pass purchases for the public Pass landing surface. DOCTRINE:
// never fabricate; never charge here — purchase wiring is the route
// wrapper's navigate-only callback. Real tables.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type {
  IntlAccaPassPurchase,
  IntlAccaTournament,
} from "../db/intl-acca-types.ts";

export interface IntlAccaPassVM {
  loading: boolean;
  error: string | null;
  availableTournaments: IntlAccaTournament[];
  userPasses: IntlAccaPassPurchase[];
  refetch: () => Promise<void>;
}

export function useIntlAccaPass(): IntlAccaPassVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const { user } = useAuthSession();
  const [s, setS] = useState<Omit<IntlAccaPassVM, "refetch">>({
    loading: true, error: null, availableTournaments: [], userPasses: [],
  });

  const fetchAll = useCallback(async () => {
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const [tRes, pRes] = await Promise.all([
        supabase.from("intl_acca_tournaments").select("*")
          .in("status", ["PRE_TOURNAMENT", "ACTIVE"]).order("start_date"),
        user
          ? supabase.from("intl_acca_pass_purchases").select("*").eq("user_id", user.id)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (tRes.error) throw tRes.error;
      setS({
        loading: false, error: null,
        availableTournaments: (tRes.data ?? []) as IntlAccaTournament[],
        userPasses: (pRes.data ?? []) as IntlAccaPassPurchase[],
      });
    } catch (e) {
      setS({
        loading: false, error: e instanceof Error ? e.message : String(e),
        availableTournaments: [], userPasses: [],
      });
    }
  }, [supabase, user]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
